import React, { useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import Button from 'common/components/Button'
import Icon from 'common/components/Icon'
import Grid from '../Grid'
import SectionOptions from './SectionOptions/SectionOptions'
import styled from 'react-emotion'
import { space, rowHeight, z } from '../../../style'
import useToggleMenu from '../../../hooks/useToggleMenu'
import usePopper from 'common/hooks/usePopper'
import { useTranslation } from 'react-i18next'

const buttonInset = 5
const buttonSize = 20

export const Section = styled('div')(({ color, height, first, editMode }) => ({
  label: 'Section',
  position: 'relative',
  marginTop: first && rowHeight.default,
  height,
  backgroundColor: color || 'transparent',
  touchAction: 'initial', // override react-dragable hack that breaks android-chrome (https://github.com/mzabriskie/react-draggable/issues/51#issuecomment-103488278)

  '&:after': editMode && {
    content: '""',
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: 0,
    opacity: 0.3,
    borderTop: '1px solid white',
    outline: '1px dashed #333',
    outlineOffset: -1,
    transform: 'translateY(0.5px) translateZ(0)',
    zIndex: 1
  }
}))

export const SectionUnderToolbar = styled('div')(({ color, editMode }) => ({
  label: 'SectionUnderToolbar',

  position: 'absolute',
  top: -rowHeight.default,
  height: rowHeight.default,
  left: 0,
  right: 0,
  backgroundColor: color || 'transparent',
  zIndex: z.toolbarColor,

  '&:after': editMode && {
    content: '""',
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: 0,
    opacity: 0.3,
    borderTop: '1px solid white',
    outline: '1px dashed #333',
    outlineOffset: -1,
    transform: 'translateY(0.5px) translateZ(0)',
    zIndex: 1
  }
}))

export const SectionNumber = styled(
  React.forwardRef((props, ref) => <Button {...props} ref={ref} />)
)({
  label: 'SectionNumber',
  position: 'sticky',
  bottom: buttonInset,
  top: buttonInset + rowHeight.default,
  zIndex: z.sectionControl
})

export const SectionNumberWrapper = styled('div')({
  label: 'SectionNumberWrapper',
  position: 'absolute',
  top: 20,
  right: buttonInset,
  bottom: 20,
  // flex needed to make the button & menu sit in the right place before position: sticky
  display: 'flex',
  alignItems: 'flex-end'
})

export const SectionResize = styled('div')({
  label: 'SectionResize',
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  marginBottom: -5,
  height: 10,
  cursor: 'ns-resize',
  zIndex: z.sectionControl
})
// TODO 3 combine below
export const SectionResizeButton = styled(Button)(({ location }) => ({
  label: 'SectionResizeButton',
  position: 'absolute',
  bottom: '50%',
  left: location || buttonInset,
  marginBottom: -buttonSize/2
}))

export const SectionInsert = styled(Button)({
  label: 'SectionInsert',
  position: 'absolute',
  bottom: 0,
  right: buttonInset,
  marginBottom: -buttonSize/2,
  zIndex: z.sectionControl
})

export const SectionInsertTop = styled(Button)({
  label: 'SectionInsert',
  position: 'absolute',
  top: 0,
  right: buttonInset,
  zIndex: z.sectionControl
})

const Res = () => {
  const { t } = useTranslation()
  // const [buttonLocation, setButtonLocation] = useState()
  // const handleMouseMove = useCallback(e => {
  //   if (hideTimeout.current) clearTimeout(hideTimeout.current)
  //   setButtonLocation(e.pageX - left.current - 10)
  // })
  // const hideTimeout = useRef()
  // const handleMouseOut = useCallback(() => {
  //   hideTimeout.current = setTimeout(() => setButtonLocation(null), 400)
  // })
  // const barRef = useRef()
  // const left = useRef()
  // useEffect(() => {
  //   // store left offset of bar
  //   if (barRef.current)
  //     left.current = barRef.current.getBoundingClientRect().left
  //   // clear timeout on unmount
  //   return () => {
  //     if (hideTimeout.current) clearTimeout(hideTimeout.current)
  //   }
  // }, [barRef])
  return (
    <SectionResize
      // onMouseMove={handleMouseMove}
      // onMouseOut={handleMouseOut}
      // innerRef={barRef}
    >
      {/* [↕] */}
      <SectionResizeButton
        // location={buttonLocation}
        rounded
        tooltip={{
          text: t('section.tooltip.resize'),
          options: { placement: 'left' }
        }}
      >
        <Icon icon="arrow-up-down" />
      </SectionResizeButton>
    </SectionResize>
  )
}

export default ({ height, first, section, createSection, scrollToSection, editMode, t }) => {
  const menuRef = useRef()
  const [menuOpen, showMenu, hideMenu] = useToggleMenu(menuRef)
  const showMenuOnly = useCallback(
    e => {
      e.stopPropagation()
      showMenu()
    },
    [showMenu]
  )

  const menuButtonRef = useRef()
  const { style: menuStyle, outOfBoundaries } = usePopper(
    {
      referenceRef: menuButtonRef,
      popperRef: menuRef,
      placement: 'top-end',
      modifiers: {
        offset: { offset: `0, ${space.small}` },
        preventOverflow: { padding: { top: 40, bottom: 10 } }
      }
    },
    [menuOpen]
  )
  return (
    <Section
      height={height}
      color={section.color}
      editMode={editMode}
      first={first}
    >
      {first && (
        <SectionUnderToolbar color={section.color} editMode={editMode} />
      )}
      {editMode && (
        <>
          <Grid />

          <Res />

          {/* {first && (
            <SectionInsertTop
              rounded
              onClick={() => createSection(section.number)}
              tooltip={{
                text: t('section.tooltip.insert', {
                  sectionNumber: section.number + 1
                }),
                options: { placement: 'left' }
              }}
            >
              <Icon icon="plus" />
            </SectionInsertTop>
          )} */}

          {/* [1] Section number */}
          <SectionNumberWrapper>
            <SectionNumber
              rounded
              // use onMouseDown for compatibility with onClickOutside
              onMouseDown={e => {
                e.stopPropagation()
              }}
              tooltip={{
                text: t('section.tooltip.options'),
                options: { placement: 'left' }
              }}
              onClick={showMenuOnly}
              active={menuOpen}
              innerRef={menuButtonRef}
            >
              {t('section.title', {
                number: section.number + 1
              })}
            </SectionNumber>
            {menuOpen &&
              ReactDOM.createPortal(
                <SectionOptions
                  hide={hideMenu}
                  innerRef={menuRef}
                  style={menuStyle}
                  scrollToSection={scrollToSection}
                  outOfBoundaries={outOfBoundaries}
                  sectionId={section.id}
                />,
                document.body
              )}
          </SectionNumberWrapper>

          <SectionInsert
            rounded
            onClick={() => createSection(section.number + 1)}
            tooltip={{
              text: t('section.tooltip.insert', {
                sectionNumber: section.number + 1
              }),
              options: { placement: 'left' }
            }}
          >
            <Icon icon="plus" />
          </SectionInsert>
        </>
      )}
    </Section>
  )
}
